import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

export interface CronParts {
  minute: string;
  hour: string;
  dayOfMonth: string;
  month: string;
  dayOfWeek: string;
}

@Component({
  selector: 'app-cron-editor',
  templateUrl: './cron-editor.component.html',
  styleUrls: ['./cron-editor.component.scss'],
  standalone: false
})
export class CronEditorComponent implements OnInit {
  @Input() expression: string = '';
  @Input() isSaving: boolean = false;
  /** When true, hides the modal chrome (header, action buttons) and emits on every change */
  @Input() inline: boolean = false;
  @Output() expressionChange = new EventEmitter<string>();
  @Output() close = new EventEmitter<void>();

  parts: CronParts = {
    minute: '*',
    hour: '*',
    dayOfMonth: '*',
    month: '*',
    dayOfWeek: '*'
  };

  presets = [
    { label: 'Every minute', value: '* * * * *' },
    { label: 'Every 5 minutes', value: '*/5 * * * *' },
    { label: 'Every 15 minutes', value: '*/15 * * * *' },
    { label: 'Every hour', value: '0 * * * *' },
    { label: 'Every day at midnight', value: '0 0 * * *' },
    { label: 'Every day at noon', value: '0 12 * * *' },
    { label: 'Every Monday at 9am', value: '0 9 * * 1' },
    { label: 'Every weekday at 9am', value: '0 9 * * 1-5' },
    { label: 'First of every month', value: '0 0 1 * *' }
  ];

  hours = Array.from({ length: 24 }, (_, i) => ({ value: String(i), label: `${i}:00` }));
  minutes = Array.from({ length: 60 }, (_, i) => ({ value: String(i), label: String(i).padStart(2, '0') }));
  daysOfWeek = [
    { value: '0', label: 'Sun' },
    { value: '1', label: 'Mon' },
    { value: '2', label: 'Tue' },
    { value: '3', label: 'Wed' },
    { value: '4', label: 'Thu' },
    { value: '5', label: 'Fri' },
    { value: '6', label: 'Sat' }
  ];

  ngOnInit(): void {
    if (this.expression) {
      this.parseExpression(this.expression);
    }
  }

  parseExpression(expr: string): void {
    const parts = expr.trim().split(/\s+/);
    if (parts.length >= 5) {
      this.parts = {
        minute: parts[0],
        hour: parts[1],
        dayOfMonth: parts[2],
        month: parts[3],
        dayOfWeek: parts[4]
      };
    }
  }

  buildExpression(): string {
    return `${this.parts.minute} ${this.parts.hour} ${this.parts.dayOfMonth} ${this.parts.month} ${this.parts.dayOfWeek}`;
  }

  onPartChange(): void {
    this.expression = this.buildExpression();
    if (this.inline) {
      this.expressionChange.emit(this.expression);
    }
  }

  applyPreset(preset: { label: string; value: string }): void {
    this.expression = preset.value;
    this.parseExpression(this.expression);
    if (this.inline) {
      this.expressionChange.emit(this.expression);
    }
  }

  save(): void {
    this.expressionChange.emit(this.expression);
    // Don't close here - parent will close after save completes
  }

  cancel(): void {
    if (!this.isSaving) {
      this.close.emit();
    }
  }

  clear(): void {
    this.expressionChange.emit('');
    // Don't close here - parent will close after save completes
  }

  getHumanReadable(): string {
    const expr = this.expression;
    if (!expr) return 'Not scheduled';

    // Simple human-readable descriptions for common patterns
    const preset = this.presets.find(p => p.value === expr);
    if (preset) return preset.label;

    const parts = expr.split(/\s+/);
    if (parts.length < 5) return expr;

    const [min, hour, dom, month, dow] = parts;

    if (min === '*' && hour === '*' && dom === '*' && month === '*' && dow === '*') {
      return 'Every minute';
    }

    if (min.startsWith('*/') && hour === '*' && dom === '*' && month === '*' && dow === '*') {
      return `Every ${min.slice(2)} minutes`;
    }

    if (hour !== '*' && min !== '*' && dom === '*' && month === '*' && dow === '*') {
      return `Daily at ${hour}:${min.padStart(2, '0')}`;
    }

    return expr;
  }
}
